import Mustache from "mustache"

// `view` feeds the Mustache SEO templates (title/description).
// `social` drives the Open Graph + Twitter card tags:
//   type       — og:type, defaults to "website"
//   card       — twitter:card override; defaults to summary_large_image
//                whenever a content image is supplied
//   image      — absolute or root-relative image URL; falls back to the site logo
//   imageAlt   — og:image:alt, defaults to the page title
//   releaseDate — video:release_date for og video objects
//   imageWidth / imageHeight — og:image dimensions, only with a content image
// `options.siteTemplates: false` ignores the site's per-path templates
// (`utils/sitePageSeo.js`), so the page's own title and description always
// apply. Use it on pages whose SEO copy is the same on every site.
// `options.canonicalPage` makes a paginated page past the first its own
// canonical (`?page=N`) instead of pointing at page 1, and ends its title with
// " – Page N" so no two pages of the list share a title, per-site templates
// included.
// `options.canonicalPath` points the canonical at another path that renders
// the same page (/series/x and /sites/x), instead of the page's own. It may
// carry a query that makes a different list (/models?sort=name); the page
// number is added to it.
// `options.titleSuffix` ends the title with " – {suffix}", before any page
// number, so lists that share copy (the /models sorts) keep their own titles.
// `options.noindex` marks a page `noindex, follow` even in production.
export const useSiteSeo = (
  view = {},
  social = {},
  {
    siteTemplates = true,
    canonicalPage = 1,
    canonicalPath = null,
    titleSuffix = null,
    noindex = false,
  } = {},
) => {
  const route = useRoute()
  const currentSite = inject("currentSite")
  const requestUrl = useRequestURL()

  const site = computed(() => {
    if (currentSite.value.alias) {
      return currentSite.value.alias
    } else {
      return currentSite.value
    }
  })

  const template = computed(() => {
    if (!siteTemplates) return null

    return sitePageTemplate(site.value.host, route.path)
  })

  const decoratedView = computed(() => {
    return {
      siteGender: site.value.gender === "girls" ? "Girls" : "Guys",
      siteName: site.value.seoSiteName,
      siteNameWithTld: site.value.seoSiteNameWithTld,
      siteNameWithSpaces: site.value.seoSiteNameWithSpaces,
      siteNameWithoutSpaces: site.value.seoSiteNameWithoutSpaces,
      ...view,
    }
  })

  const title = computed(() => {
    const rendered = template.value?.title
      ? Mustache.render(template.value.title, decoratedView.value)
      : Mustache.render(
          view.defaultTitle || site.value.seoDefaultTitle || "",
          decoratedView.value,
        )

    const suffix = toValue(titleSuffix)
    const titled = suffix ? `${rendered.trim()} – ${suffix}` : rendered
    const page = parseInt(toValue(canonicalPage)) || 1
    return page > 1 ? `${titled.trim()} – Page ${page}` : titled
  })

  const description = computed(() => {
    if (template.value?.description) {
      return Mustache.render(template.value.description, decoratedView.value)
    } else {
      return Mustache.render(
        view.defaultDescription || site.value.seoDefaultDescription || "",
        decoratedView.value,
      )
    }
  })

  // Social crawlers ignore root-relative URLs, so everything they consume has
  // to be resolved against the host that served the page. Already-absolute
  // URLs (CDN assets) pass through untouched.
  //
  // CloudFront talks to the origin over plain HTTP, so the request URL says
  // http:// in production. Every public host is HTTPS-only; only local dev
  // hosts keep the scheme they were served on.
  const siteOrigin = computed(() => {
    const origin = new URL(requestUrl.origin)
    const local = /(^localhost$|^127\.|\.lvh\.me$|\.lan$)/.test(origin.hostname)
    if (!local) origin.protocol = "https:"
    return origin.origin
  })

  const absoluteUrl = (path) => {
    if (!path) return null

    try {
      return new URL(path, siteOrigin.value).href
    } catch {
      return null
    }
  }

  // Path only: query strings (sorting, ?geo=, tracking) never make a
  // different page. The exception is a page number the page opts into.
  const canonicalUrl = computed(() => {
    const page = parseInt(toValue(canonicalPage)) || 1
    const path = toValue(canonicalPath) || route.path
    const join = path.includes("?") ? "&" : "?"
    return absoluteUrl(page > 1 ? `${path}${join}page=${page}` : path)
  })

  // Account and funnel pages have nothing worth ranking, and nothing outside
  // production should be indexed — staging is public and its canonical
  // points at itself, so it would compete with the live site.
  const isProduction = useRuntimeConfig().public.appEnv === "production"
  const noindexPrefixes = ["/members", "/login", "/welcome", "/tour/"]
  const robots = computed(() =>
    !isProduction ||
    toValue(noindex) ||
    noindexPrefixes.some((prefix) => route.path.startsWith(prefix))
      ? "noindex, follow"
      : "index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1",
  )

  // When a release has no handtouched image for the requested variant (common
  // for the SFW poster), the serializer still signs a URL — it just comes back
  // path-less, e.g. "https://cdn-images…/?imgw=970&imgh=545", which the CDN
  // answers with a 403. Advertising that would give crawlers a broken card, so
  // treat a path-less URL as "no image" and let the site logo take over.
  const usableImageUrl = (path) => {
    const href = absoluteUrl(path)
    return isUsableImageUrl(href) ? href : null
  }

  const contentImage = computed(() => usableImageUrl(toValue(social.image)))

  const imageUrl = computed(
    () =>
      contentImage.value ||
      absoluteUrl(`/images/sites/${currentSite.value.slug}/logo.png`),
  )

  const isoDate = (value) => {
    if (!value) return null

    const date = new Date(value)
    return Number.isNaN(date.getTime()) ? null : date.toISOString()
  }

  const socialMeta = computed(() => {
    const alt = toValue(social.imageAlt) || title.value

    const tags = [
      { property: "og:type", content: social.type || "website" },
      { property: "og:site_name", content: site.value.seoSiteName },
      { property: "og:title", content: title.value },
      { property: "og:description", content: description.value },
      { property: "og:url", content: canonicalUrl.value },
      { property: "og:locale", content: "en_US" },
      {
        name: "twitter:card",
        content:
          social.card ||
          (contentImage.value ? "summary_large_image" : "summary"),
      },
      { name: "twitter:title", content: title.value },
      { name: "twitter:description", content: description.value },
    ]

    if (imageUrl.value) {
      tags.push(
        { property: "og:image", content: imageUrl.value },
        { property: "og:image:alt", content: alt },
        { name: "twitter:image", content: imageUrl.value },
        { name: "twitter:image:alt", content: alt },
      )
    }

    if (contentImage.value && social.imageWidth && social.imageHeight) {
      tags.push(
        { property: "og:image:width", content: String(social.imageWidth) },
        { property: "og:image:height", content: String(social.imageHeight) },
      )
    }

    // NOTE: no video:actor — unhead only lets og:/twitter:/article: namespaces
    // repeat, so a cast list would collapse to a single arbitrary name.
    const releaseDate = isoDate(toValue(social.releaseDate))
    if (releaseDate) {
      tags.push({ property: "video:release_date", content: releaseDate })
    }

    return tags.filter((tag) => tag.content)
  })

  useHead({
    title,
    link: computed(() => [{ rel: "canonical", href: canonicalUrl.value }]),
    meta: computed(() => [
      {
        name: "description",
        content: description.value,
      },
      {
        name: "robots",
        content: robots.value,
      },
      ...socialMeta.value,
    ]),
  })
}
